/**
 * Offer URL Builder
 *
 * Utility for building offer page URLs with search parameters.
 * Appends date, duration, and passenger information to product URLs.
 *
 * @example
 * ```ts
 * const url = buildOfferUrl(
 *   'https://example.com/product',
 *   [new Date('2025-11-15'), new Date('2025-11-22')],
 *   7,
 *   { rooms: [{ persons: [{ type: 'adult' }, { type: 'adult' }] }] }
 * );
 * // "https://example.com/product?date=2025-11-15&todate=2025-11-22&days=7&pax=r35-35"
 * ```
 */

import type { DateRange, RoomOccupancy } from '@/types/SearchBar';
import type { DurationMode } from '@/utils/filters/durationMode';
import { serializePaxString } from '@/utils/parsing/paxParser';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';

/**
 * Extra parameters of an offer deep link.
 *
 * `offerRange` are the travel dates of the single priced offer the link was
 * built from. The search window in `dateRange` stays untouched, so the detail
 * page keeps offering the other departures the user could still pick; the
 * offer's own dates ride along as `odate` / `otodate` so the detail page can
 * surface the exact offer that was clicked.
 */
export interface OfferUrlOptions {
  durationMode?: DurationMode;
  offerRange?: DateRange;
}

/**
 * Build offer page URL with search parameters
 *
 * Appends query parameters for date range, duration, and room occupancy.
 * Handles existing query parameters in base URL.
 *
 * @param baseUrl - Base product URL (from NBC API pdpUrl)
 * @param dateRange - Start and end dates of the search window
 * @param duration - Trip duration in days
 * @param roomOccupancy - Room and passenger information
 * @param options - Duration intent and, for a link built from one priced offer,
 *   that offer's own dates
 * @returns Complete URL with search parameters
 *
 * @example
 * ```ts
 * // Simple case
 * buildOfferUrl(
 *   'https://example.com/product',
 *   [new Date('2025-11-15'), new Date('2025-11-22')],
 *   7,
 *   { rooms: [{ persons: [{ type: 'adult' }, { type: 'adult' }] }] }
 * );
 * // "https://example.com/product?date=2025-11-15&todate=2025-11-22&days=7&pax=r35-35"
 *
 * // URL with existing query params
 * buildOfferUrl(
 *   'https://example.com/product?source=newsletter',
 *   [new Date('2025-11-15'), new Date('2025-11-22')],
 *   7,
 *   { rooms: [{ persons: [{ type: 'adult' }] }] }
 * );
 * // "https://example.com/product?source=newsletter&date=2025-11-15&todate=2025-11-22&days=7&pax=r35"
 * ```
 */
export function buildOfferUrl(
  baseUrl: string,
  dateRange: DateRange,
  duration: number,
  roomOccupancy: RoomOccupancy,
  options: OfferUrlOptions = {}
): string {
  const { durationMode, offerRange } = options;

  if (!baseUrl || !dateRange || !duration || !roomOccupancy) {
    console.warn('[offerUrlBuilder] Missing required parameters, returning base URL');
    return baseUrl;
  }

  const [startDate, endDate] = dateRange;

  if (!startDate || !endDate) {
    console.warn('[offerUrlBuilder] Invalid date range, returning base URL');
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl, window.location.origin);

    url.searchParams.set('date', formatDate(startDate));
    url.searchParams.set('todate', formatDate(endDate));
    url.searchParams.set('days', duration.toString());

    if (durationMode === 'exact') {
      url.searchParams.set('dmode', 'exact');
    }

    const [offerStart, offerEnd] = offerRange ?? [];
    if (offerStart && offerEnd) {
      url.searchParams.set('odate', formatDate(offerStart));
      url.searchParams.set('otodate', formatDate(offerEnd));
    }

    const paxString = serializePaxString(roomOccupancy);
    if (paxString) {
      url.searchParams.set('pax', paxString);
    } else {
      console.warn('[offerUrlBuilder] Failed to serialize pax string');
    }

    // Preserve debug parameter from current URL
    const currentParams = new URLSearchParams(window.location.search);
    const debugValue = currentParams.get('debug');
    if (debugValue) {
      url.searchParams.set('debug', debugValue);
    }

    return url.toString();
  } catch (error) {
    console.error('[offerUrlBuilder] Error building URL:', error);
    return baseUrl;
  }
}

/**
 * Format Date object to YYYY-MM-DD string
 *
 * Reads local Y/M/D so date-picker output (local-midnight `Date`) round-trips
 * through the URL as the day the user actually picked. Plain `toISOString()`
 * would shift the calendar day for any positive-offset TZ.
 */
function formatDate(date: Date): string {
  return formatDateLocal(date);
}
